import { useState } from 'react';
import { PageHeader } from '@/components/composites/PageHeader';
import { AppIcon } from '@/components/primitives/AppIcon';
import { Button } from '@/components/primitives/Button';
import { Checkbox } from '@/components/primitives/Checkbox';
import { Select } from '@/components/primitives/Select';
import { ActionLink } from '@/components/primitives/ActionLink';
import type { CategorizeGroup } from '@/data/categorizeGroups';
import { useCategorizeGroups } from '@/hooks/useCategorizeGroups';
import styles from './CategorizeTimePage.module.css';

type GroupStatus = 'neutral' | 'assigned' | 'skipped';

function ProgressIndicator({
  resolvedCount,
  totalGroups,
  assignedHours,
  totalHours,
}: {
  resolvedCount: number;
  totalGroups: number;
  assignedHours: string;
  totalHours: string;
}) {
  const pct = totalGroups > 0 ? (resolvedCount / totalGroups) * 100 : 0;
  return (
    <div className={styles.progress}>
      <span className={styles.progressText}>
        <span className={styles.mono}>{resolvedCount}</span> of{' '}
        <span className={styles.mono}>{totalGroups}</span> groups resolved{' \u00B7 '}
        <span className={styles.mono}>{assignedHours}</span> of{' '}
        <span className={styles.mono}>{totalHours}</span> assigned
      </span>
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function AssignToolbar({
  selectedCount,
  toolbarClient,
  toolbarProject,
  clientOpts,
  projectOpts,
  onClientChange,
  onProjectChange,
  onAssign,
  onSkip,
}: {
  selectedCount: number;
  toolbarClient: string;
  toolbarProject: string;
  clientOpts: readonly { value: string; label: string }[];
  projectOpts: readonly { value: string; label: string }[];
  onClientChange: (v: string) => void;
  onProjectChange: (v: string) => void;
  onAssign: () => void;
  onSkip: () => void;
}) {
  return (
    <div className={styles.toolbar}>
      <span className={styles.toolbarLabel}>Assign to</span>
      <Select
        value={toolbarClient}
        options={clientOpts}
        placeholder="Client\u2026"
        onChange={onClientChange}
        size="sm"
      />
      <Select
        value={toolbarProject}
        options={projectOpts}
        placeholder="Project\u2026"
        onChange={onProjectChange}
        size="sm"
      />
      <span className={styles.toolbarDivider} />
      <Button variant="primary" onClick={onAssign} disabled={selectedCount === 0} size="sm">
        Assign
      </Button>
      <Button variant="ghost" onClick={onSkip} disabled={selectedCount === 0} size="sm">
        Skip
      </Button>
      {selectedCount > 0 && <span className={styles.toolbarCount}>{selectedCount} selected</span>}
      {selectedCount === 0 && (
        <span className={styles.toolbarHint}>Select groups to assign or skip</span>
      )}
    </div>
  );
}

function GroupCard({
  group,
  isSelected,
  isExpanded,
  status,
  onToggleSelect,
  onToggleExpand,
}: {
  group: CategorizeGroup;
  isSelected: boolean;
  isExpanded: boolean;
  status: GroupStatus;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
}) {
  const cardClass = [
    styles.groupCard,
    isSelected ? styles.groupSelected : '',
    status === 'assigned' ? styles.groupAssigned : '',
    status === 'skipped' ? styles.groupSkipped : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cardClass} data-testid="categorization-group">
      {/* Group header */}
      <div className={styles.groupHeader}>
        <div className={styles.groupHeaderLeft}>
          <Checkbox
            checked={isSelected}
            onChange={onToggleSelect}
            label={`Select ${group.title}`}
            disabled={status !== 'neutral'}
          />
          <AppIcon app={group.appKey} size={36} />
          <div className={styles.groupInfo}>
            <span className={styles.groupTitle}>{group.title}</span>
            <span className={styles.groupMeta}>
              {group.eventCount} events &middot; {group.dateRange}
            </span>
          </div>
        </div>
        <div className={styles.groupHeaderRight}>
          <div className={styles.groupKpi}>
            <span className={`${styles.groupKpiValue} ${styles.mono}`}>
              {group.hours.toFixed(1)}h
            </span>
            <span className={styles.groupKpiLabel}>hours</span>
          </div>
          <div className={styles.groupKpi}>
            <span className={`${styles.groupKpiValue} ${styles.mono}`}>{group.eventCount}</span>
            <span className={styles.groupKpiLabel}>events</span>
          </div>
          {status === 'assigned' && <span className={styles.badgeDone}>Assigned</span>}
          {status === 'skipped' && <span className={styles.badgeSkipped}>Skipped</span>}
          <button
            className={styles.expandBtn}
            onClick={onToggleExpand}
            aria-label={isExpanded ? 'Collapse events' : 'Expand events'}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={isExpanded ? '' : styles.collapsed}
            >
              <path d="M4 6l4 4 4-4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Event table */}
      {isExpanded && (
        <div className={styles.groupBody}>
          <div className={styles.eventTableWrap}>
            <table className={styles.eventTable}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Timestamp</th>
                  <th>Window Title</th>
                  <th className={styles.right}>Duration</th>
                  <th>Override Assignment</th>
                </tr>
              </thead>
              <tbody>
                {group.events.map((evt) => (
                  <tr key={evt.id}>
                    <td>
                      <Checkbox checked={false} onChange={() => {}} />
                    </td>
                    <td className={styles.mono}>{evt.timestamp}</td>
                    <td>{evt.windowTitle}</td>
                    <td className={`${styles.right} ${styles.mono}`}>{evt.duration}</td>
                    <td>&mdash;</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {group.events.length < group.eventCount && (
            <div className={styles.showMore}>
              Showing {group.events.length} of {group.eventCount} events
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PageFooter({
  resolvedCount,
  totalGroups,
}: {
  resolvedCount: number;
  totalGroups: number;
}) {
  return (
    <div className={styles.footer}>
      <div className={styles.footerLeft}>
        <ActionLink href="#">&larr; Back to Sarah Chen</ActionLink>
        <span className={styles.footerStatus}>
          {resolvedCount} of {totalGroups} groups resolved
        </span>
      </div>
      <div className={styles.footerRight}>
        <Button variant="ghost" size="sm">
          Done for now
        </Button>
        <Button variant="primary" size="sm" disabled>
          All resolved
        </Button>
      </div>
    </div>
  );
}

export function CategorizeTimePage() {
  const {
    groups: categorizeGroups,
    clients: categorizeClients,
    projects: categorizeProjects,
    totalHours,
  } = useCategorizeGroups();

  const clientOptions = categorizeClients.map((c) => ({ value: c, label: c }));
  const projectOptions = categorizeProjects.map((p) => ({ value: p, label: p }));

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [statuses, setStatuses] = useState<Record<string, GroupStatus>>({});
  const [toolbarClient, setToolbarClient] = useState('');
  const [toolbarProject, setToolbarProject] = useState('');

  const toggleSelect = (id: string) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedIds = categorizeGroups.filter((g) => selected[g.id]).map((g) => g.id);
  const selectedCount = selectedIds.length;

  const resolvedCount = categorizeGroups.filter(
    (g) => statuses[g.id] === 'assigned' || statuses[g.id] === 'skipped',
  ).length;

  const assignedHoursNum = categorizeGroups
    .filter((g) => statuses[g.id] === 'assigned')
    .reduce((sum, g) => sum + g.hours, 0);

  const handleAssign = () => {
    if (selectedCount === 0) return;
    const next = { ...statuses };
    selectedIds.forEach((id) => {
      next[id] = 'assigned';
    });
    setStatuses(next);
    setSelected({});
  };

  const handleSkip = () => {
    if (selectedCount === 0) return;
    const next = { ...statuses };
    selectedIds.forEach((id) => {
      next[id] = 'skipped';
    });
    setStatuses(next);
    setSelected({});
  };

  return (
    <>
      <PageHeader title="Categorize Captured Time" />

      <p className={styles.subtitle}>
        Review uncategorized activity groups and assign each to a client and project, or skip groups
        that aren&rsquo;t relevant.
      </p>

      <ProgressIndicator
        resolvedCount={resolvedCount}
        totalGroups={categorizeGroups.length}
        assignedHours={`${assignedHoursNum.toFixed(1)}h`}
        totalHours={totalHours}
      />

      <AssignToolbar
        selectedCount={selectedCount}
        toolbarClient={toolbarClient}
        toolbarProject={toolbarProject}
        clientOpts={clientOptions}
        projectOpts={projectOptions}
        onClientChange={setToolbarClient}
        onProjectChange={setToolbarProject}
        onAssign={handleAssign}
        onSkip={handleSkip}
      />

      <div className={styles.groups}>
        {categorizeGroups.map((group) => (
          <GroupCard
            key={group.id}
            group={group}
            isSelected={!!selected[group.id]}
            isExpanded={!!expanded[group.id]}
            status={statuses[group.id] || 'neutral'}
            onToggleSelect={() => toggleSelect(group.id)}
            onToggleExpand={() => toggleExpand(group.id)}
          />
        ))}
      </div>

      <PageFooter resolvedCount={resolvedCount} totalGroups={categorizeGroups.length} />
    </>
  );
}
